import type { BulletSystem } from '@/entities/bullets';
import { KR, type Arm } from './kraken-parts';

/** Where the guns sit this step (mantle + arms) — handed in by the boss. */
export interface KrakenRig {
  x: number;
  y: number;
  driftAngle: number;
  time: number;
  arms: ReadonlyArray<Arm>;
  enraged: boolean;
}

/**
 * Kraken's bespoke fire (C7): a sparse heavy mantle ring + arm tips taking
 * turns to spit, then the enraged ink spiral. Cadence follows the tier tempo.
 */
export class KrakenGuns {
  private tipFireTimer: number = KR.tipFireInterval;
  private ringTimer: number = KR.ringInterval;
  private spiralTimer = 0;
  private spiralAngle = 0;

  constructor(private readonly tempo: number, private readonly shot: number) {}

  update(rig: KrakenRig, dt: number, px: number, py: number, bullets: BulletSystem): void {
    if (rig.enraged) {
      // Ink spiral — two rotating jets
      this.spiralTimer -= dt;
      if (this.spiralTimer <= 0) {
        this.spiralTimer = KR.spiralInterval / this.tempo;
        this.spiralAngle += (KR.spiralDegPerShot * Math.PI) / 180;
        this.spawn(bullets, rig.x, rig.y, this.spiralAngle, 170, 5, 1, 'ball');
        this.spawn(bullets, rig.x, rig.y, this.spiralAngle + Math.PI, 170, 5, 1, 'ball');
      }
      return;
    }
    // Phase 1: sparse heavy ring from the mantle
    this.ringTimer -= dt * this.tempo;
    if (this.ringTimer <= 0) {
      this.ringTimer = KR.ringInterval;
      for (let i = 0; i < KR.ringCount; i++) {
        const a = (Math.PI * 2 * i) / KR.ringCount + rig.driftAngle;
        this.spawn(bullets, rig.x, rig.y, a, 110, 6, 2, 'ring');
      }
    }
    // Living tips take turns spitting an aimed shot
    this.tipFireTimer -= dt * this.tempo;
    if (this.tipFireTimer <= 0) {
      let alive = 0;
      for (const a of rig.arms) if (a.alive) alive++;
      this.tipFireTimer = KR.tipFireInterval / Math.max(1, alive * 0.45);
      const shooters = rig.arms.filter((a) => a.alive && a.lashing <= 0);
      const arm = shooters[Math.floor((rig.time * 7) % Math.max(1, shooters.length))];
      if (arm) {
        const a = Math.atan2(py - arm.tipY, px - arm.tipX);
        this.spawn(bullets, arm.tipX, arm.tipY, a, 240, 4, 1, 'ball');
      }
    }
  }

  private spawn(
    bullets: BulletSystem, x: number, y: number, angle: number,
    speed: number, life: number, damage: number, kind: 'ball' | 'ring',
  ): void {
    bullets.spawn({
      x, y, dirX: Math.cos(angle), dirY: Math.sin(angle),
      speed: speed * this.shot, life, radius: 10, damage,
      friendly: false, texKey: `bullet/enemy/${kind}`, rotates: false,
    });
  }
}
